import LockedContent from "@/components/LockedContent";
import ReadMoreLogin from "@/components/ReadMoreLogin";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import Layout from "@/components/elements/Layout";
import ShareButtons from "@/components/parts/ShareButtons";
import ShareButtonsVertical from "@/components/parts/ShareButtonsVertical";
import Schema from "@/components/schema/Schema";
import { Button } from "@/components/ui/button";
import {
  useGetContentsByContentType,
  useGetCurrentUrl,
  useGetRelatedContents,
} from "@/hooks";
import useAuth from "@/hooks/auth";
import {
  getContentDetailBySlug,
  getPinnedByContentId,
  trackContentAccessCounting,
} from "@/services/api";
import { CONTENT_TYPES, cleanHTML } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import isPersonName from "@/utils/isPersonName";
import {
  ArrowLeft,
  BookOpen,
  ArrowRight,
  LinkedinLogo,
  TwitterLogo,
  FacebookLogo,
  InstagramLogo,
} from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ImageLogo from "../../assets/images/nav-logo-vertical.svg";
import SubNavigation from "@/components/elements/SubNavigation";
import { fitIn } from "@/utils/imageHandler";
import { useAtom } from "jotai";
import { userAtom } from "@/Atoms";
import MentionedVCAndStartup from "@/components/MentionedVCAndStartup";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import AuthorContent from "@/components/AuthorContent";
import RelatedReport from "@/components/RelatedReport";

function SpotlightDetail(props) {
  const [reach_limit, setReachLimit] = useState(false);
  const { user } = useAuth();
  const [userState, setUserState] = useAtom(userAtom);
  const router = useRouter();
  const [expandedAuthors, setExpandedAuthors] = useState({});

  const [related_items, isLoading] = useGetRelatedContents(
    CONTENT_TYPES.SPOTLIGHTS,
    props.spotlight.uuid
  );

  const [spotlight, setSpotlight] = useState(props.spotlight);
  const [otherReports, otherReportLoading] = useGetContentsByContentType(
    CONTENT_TYPES.SPECIAL_REPORTS
  );

  const [pinneds, setPinneds] = useState({
    startups: [],
    ventureCapitals: [],
  });

  const loadSpotlight = async () => {
    try {
      const response = await getContentDetailBySlug(router.query.slug);
      setSpotlight(response.data.data);
    } catch {}
  };

  const fetchMeteredContent = async () => {
    if (userState?.roles === "admin-team" || userState?.roles === "read-access") {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    loadSpotlight();
    fetchMeteredContent();
    getPinned();
  }, [router.query.slug, userState, spotlight.id]);

  const fullUrl = useGetCurrentUrl();

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      mainEntityOfPage: {
        "@type": "WebPage",
        id: fullUrl,
      },
      headline: spotlight.meta_title,
      image: [spotlight.thumbnail_url],
      datePublished: spotlight.publish_date,
      dateModified: spotlight.publish_date,
      author: {
        "@type": isPersonName(spotlight.author.name)
          ? "Person"
          : "Organization",
        name: isPersonName(spotlight.author.name)
          ? spotlight.author.name
          : "Lucidity Insights",
      },
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
      description: spotlight.meta_desc,
    },
  ];

  const getPinned = async () => {
    setPinneds({
      startups: [],
      ventureCapitals: [],
    });
    try {
      const res = await getPinnedByContentId(spotlight.id);
      setPinneds(res.data.data);
    } catch (error) {
      setPinneds({
        startups: [],
        ventureCapitals: [],
      });
    }
  };

  const transformYoutubeLinks = (content) => {
    const youtubeRegex =
      /@(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:&\S+)?/g;

    return content.replace(youtubeRegex, (match, videoId) => {
      const iframe = `
        <div class="relative aspect-video w-full my-6">
          <iframe
            class="absolute top-0 left-0 w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&showinfo=0"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      `;
      return iframe;
    });
  };

  const toggleAuthorDescription = (authorId) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorId]: !prev[authorId],
    }));
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        title={spotlight.title}
        meta_title={spotlight.meta_title}
        description={spotlight.meta_desc}
        meta_image={spotlight.cover_image_url}
      >
        <SubNavigation title={spotlight.title}></SubNavigation>
        <div className='px-4 mx-auto max-w-7xl lg:flex'>
          <div className='flex items-start flex-1 pt-6 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:mr-8 lg:border-r lg:space-x-8 2xl:space-x-12 border-white/5'>
            <div className='sticky hidden pt-2 lg:block top-40'>
              <ShareButtonsVertical
                title={spotlight.title}
                description={spotlight.meta_desc}
                url={fullUrl}
              />
            </div>
            <div className='relative w-full space-y-10 lg:space-y-16 max-w-screen-2xl'>
              <article className='relative mx-auto'>
                <h1 className='text-3xl leading-[1.1] font-bold tracking-tight lg:text-5xl font-display'>
                  {spotlight.title}
                </h1>

                <div className='flex items-center justify-between gap-4 mt-3'>
                  <p className='flex items-center text-sm'>
                    <span className='opacity-40'>
                      {`${formatDate(spotlight.publish_date, "DD MMMM YYYY")}`}
                    </span>
                    <span className='ml-2 opacity-40'>&bull;</span>
                    <ContentWishlistComponent item={spotlight} />
                  </p>
                  <span className='flex-1 h-px bg-white/10'></span>
                  <div className='lg:hidden'>
                    <ShareButtons
                      title={spotlight.title}
                      description={spotlight.meta_desc}
                      url={fullUrl}
                    />
                  </div>
                </div>

                <div className='pt-3 pb-6 text-sm text-white/70'>
                  By{" "}
                  {spotlight.authors.map((author, index) => (
                    <Link
                      key={index}
                      href={`/author/${author.slug}`}
                      className='no-underline'
                    >
                      <span className='underline'>{author.name}</span>
                      {spotlight.authors.length > 2 &&
                        index < spotlight.authors.length - 2 &&
                        ", "}
                      {spotlight.authors.length > 1 &&
                        index === spotlight.authors.length - 2 &&
                        " and "}
                    </Link>
                  ))}{" "}
                </div>
                <div className='relative mb-5'>
                  <div className='relative w-full h-auto'>
                    <Image
                      src={fitIn(spotlight.cover_image_url, 1200)}
                      alt={spotlight.cover_image_alt}
                      width={640}
                      height={320}
                      layout={"responsive"}
                      objectFit={"cover"}
                      className='rounded-[15px]'
                    />
                  </div>
                  <div className='absolute top-0 -mt-10 right-4 md:right-12'>
                    <div className='bg-white rounded-[10px] w-16 h-16 md:w-24 md:h-24'>
                      <div className='w-auto h-auto'>
                        <Image
                          src={fitIn(spotlight.logo_url, 200)}
                          alt={spotlight.logo_alt}
                          width={64}
                          height={64}
                          layout={"responsive"}
                          objectFit={"cover"}
                          className='rounded-[10px] bg-transparent'
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='pt-6 mx-auto prose lg:pt-8 content prose-invert prose-img:rounded-lg'>
                  {reach_limit
                    ? cleanHTML(spotlight.description.slice(0, 850))
                    : cleanHTML(transformYoutubeLinks(spotlight.description))}
                </div>
                {reach_limit && (
                  <div className='absolute bottom-0 w-full pt-48 not-prose bg-gradient-to-b from-transparent via-background to-background'>
                    <LockedContent
                      title="You've Reached Your Monthly Limit"
                      description='You are currently on our free plan. Upgrade to enjoy unlimited access.'
                      isLogin={user?.id}
                    />
                  </div>
                )}
              </article>
              <MentionedVCAndStartup pinneds={pinneds} />

              {/* Authors Section */}
              <AuthorContent data={spotlight.authors} />
            </div>
          </div>

          <div className='lg:pt-10 lg:max-w-[340px] text-white lg:pb-16 space-y-8 lg:space-y-10'>
            <RelatedReport connectedWith={spotlight.connected_with} />
            <RelatedContentSidebar
              title='Related Videos & Podcasts'
              currentContentId={spotlight.uuid}
              contentType={CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES}
            />
            <RelatedContentSidebar
              title='Did you check these out?'
              currentContentId={spotlight.uuid}
            />
          </div>
        </div>
      </Layout>
    </div>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.SPOTLIGHTS,
      },
    });
    return {
      props: {
        spotlight: response.data.data,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default SpotlightDetail;
